import { useTheme } from "../../../contexts/ThemeContext";
import { platformFees } from "../../../mocks/dashboardMocks";

const PLATFORM_COLORS: Record<string, string> = {
  StockX: "bg-emerald-500/15 text-emerald-500",
  GOAT: "bg-amber-500/15 text-amber-500",
  eBay: "bg-sky-500/15 text-sky-500",
  Depop: "bg-rose-500/15 text-rose-500",
  Grailed: "bg-violet-500/15 text-violet-400",
  "Facebook MP": "bg-blue-500/15 text-blue-400",
  Poshmark: "bg-pink-500/15 text-pink-400",
  Mercari: "bg-red-500/15 text-red-400",
  Whatnot: "bg-orange-500/15 text-orange-400",
  Amazon: "bg-yellow-500/15 text-yellow-500",
  OfferUp: "bg-teal-500/15 text-teal-400",
  Goldin: "bg-amber-600/15 text-amber-400",
};

const PLATFORM_COLORS_LIGHT: Record<string, string> = {
  StockX: "bg-emerald-50 text-emerald-700",
  GOAT: "bg-amber-50 text-amber-700",
  eBay: "bg-sky-50 text-sky-700",
  Depop: "bg-rose-50 text-rose-700",
  Grailed: "bg-violet-50 text-violet-700",
  "Facebook MP": "bg-blue-50 text-blue-700",
  Poshmark: "bg-pink-50 text-pink-700",
  Mercari: "bg-red-50 text-red-700",
  Whatnot: "bg-orange-50 text-orange-700",
  Amazon: "bg-yellow-50 text-yellow-700",
  OfferUp: "bg-teal-50 text-teal-700",
  Goldin: "bg-amber-50 text-amber-800",
};

export default function FeeBreakdown() {
  const { isDark } = useTheme();

  return (
    <div className={`rounded-2xl border overflow-hidden ${isDark ? "bg-[#0d0d0d] border-white/6" : "bg-[#f8f8f8] border-black/6"}`}>
      {/* Header */}
      <div className={`px-6 py-5 border-b ${isDark ? "border-white/5" : "border-black/5"}`}>
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${isDark ? "bg-white/6" : "bg-black/6"}`}>
            <i className={`ri-percent-line text-base ${isDark ? "text-white/50" : "text-[#666]"}`} />
          </div>
          <div>
            <h3 className={`font-syne font-semibold text-base ${isDark ? "text-white" : "text-[#111]"}`}>
              Platform Fee Breakdown
            </h3>
            <p className={`font-inter text-xs ${isDark ? "text-[#555]" : "text-[#999]"}`}>
              Current selling fees by platform
            </p>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className={`border-b ${isDark ? "border-white/4" : "border-black/4"}`}>
              {["Platform", "Sale Fee", "Payment Fee", "Shipping", "Total Est.", "Notes"].map((h) => (
                <th
                  key={h}
                  className={`font-inter text-[10px] uppercase tracking-widest font-medium text-left px-5 py-3 ${isDark ? "text-[#3a3a3a]" : "text-[#bbb]"}`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {platformFees.map((p, i) => {
              const total = p.saleFee + p.paymentFee + (p.shipping > 0 ? 1.2 : 0);
              const colorClass = isDark ? PLATFORM_COLORS[p.platform] : PLATFORM_COLORS_LIGHT[p.platform];
              return (
                <tr
                  key={p.platform}
                  className={`border-b transition-colors duration-150 ${
                    isDark
                      ? `border-white/3 ${i % 2 === 0 ? "" : "bg-white/1"} hover:bg-white/3`
                      : `border-black/3 ${i % 2 === 0 ? "" : "bg-black/1"} hover:bg-black/3`
                  }`}
                >
                  <td className="px-5 py-4">
                    <span className={`font-inter text-xs font-semibold px-2.5 py-1 rounded-full ${colorClass ?? ""}`}>
                      {p.platform}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`font-inter text-sm font-medium ${isDark ? "text-white/80" : "text-[#222]"}`}>
                      {p.saleFee}%
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`font-inter text-sm ${isDark ? "text-[#666]" : "text-[#777]"}`}>
                      {p.paymentFee > 0 ? `${p.paymentFee}%` : "—"}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`font-inter text-sm ${isDark ? "text-[#666]" : "text-[#777]"}`}>
                      {p.shipping > 0 ? `$${p.shipping}` : "Varies"}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`font-inter text-sm font-medium ${isDark ? "text-white/70" : "text-[#333]"}`}>
                      ~{total.toFixed(1)}%
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`font-inter text-xs ${isDark ? "text-[#3a3a3a]" : "text-[#bbb]"}`}>
                      {p.notes}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className={`px-6 py-4 border-t ${isDark ? "border-white/4 bg-white/1" : "border-black/4 bg-black/1"}`}>
        <p className={`font-inter text-[11px] ${isDark ? "text-[#3a3a3a]" : "text-[#ccc]"}`}>
          <i className="ri-information-line mr-1.5" />
          Fees are estimates. Always verify on each platform&apos;s official fee schedule. Rates may change.
        </p>
      </div>
    </div>
  );
}
